import React, { useEffect, useState } from 'react';
import SmsConsentCheckbox from '../components/compliance/SmsConsentCheckbox';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Loader2, Check, ShieldCheck, Calendar, MapPin, Share2, Copy, Sparkles, Hotel, Car, ExternalLink, Bus } from 'lucide-react';
import { coupleNames as fmtCouple } from '../utils/coupleNames';

// Turn bare URLs inside plain text into real anchor tags so couples who
// paste a wedding-site link into their custom thank-you (or any other RSVP
// message) don't leave their guests staring at un-tappable text.
function linkifyText(input) {
  if (input === null || input === undefined) return null;
  const text = String(input);
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      // Strip trailing punctuation that shouldn't be part of the link.
      const m = part.match(/^(.*?)([.,!?;:)\]]*)$/);
      const href = m ? m[1] : part;
      const trail = m ? m[2] : '';
      return (
        <React.Fragment key={i}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`linkified-url-${i}`}
            className="text-[#7B2C3B] underline underline-offset-4 decoration-[#7B2C3B]/40 hover:decoration-[#7B2C3B] break-all"
          >{href}</a>
          {trail}
        </React.Fragment>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Rsvp() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // If a guest already submitted their RSVP (e.g. via SMS) and then clicks
  // the email link, we don't want to make them re-do it. Show a friendly
  // "we've got your reply" popup with an option to still edit.
  const [showRepliedModal, setShowRepliedModal] = useState(false);

  const [form, setForm] = useState({
    rsvp: 'Pending', plusOneComing: null, plusOneName: '', meal: '—', plusOneMeal: '—', dietary: '', address: '', phone: '', email: '', notes: '', customAnswer: '', smsConsent: false,
  });
  const [emailWasBlank, setEmailWasBlank] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/guest-portal/public/${token}`);
        setData(data);
        setEmailWasBlank(!(data.guest.email || '').trim());
        // Auto-pop the "we already have your RSVP" modal on load if they've
        // already answered. Skips only for Pending guests (still needs to reply).
        if (data.guest.rsvp === 'Yes' || data.guest.rsvp === 'No') {
          setShowRepliedModal(true);
        }
        setForm({
          rsvp: data.guest.rsvp || 'Pending',
          // Eligible guests default to Yes if they haven't answered; ineligible
          // guests keep this at null (the section is hidden for them anyway).
          plusOneComing: (data.guest.plusOneComing === null || data.guest.plusOneComing === undefined)
            ? (data.guest.plusOne ? true : null)
            : !!data.guest.plusOneComing,
          plusOneName: data.guest.plusOneName || '',
          meal: data.guest.meal || '—',
          plusOneMeal: data.guest.plusOneMeal || '—',
          dietary: data.guest.dietary || '',
          address: data.guest.address || '',
          phone: data.guest.phone || '',
          email: data.guest.email || '',
          notes: data.guest.notes || '',
          customAnswer: data.guest.customAnswer || '',
        });
      } catch (e) {
        setError(e?.response?.data?.detail || 'Invalid or expired link');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // If they said "No plus one", clear the partner name so headcount is accurate.
      const payload = { ...form };
      if (payload.plusOneComing === false) { payload.plusOneName = ''; payload.plusOneMeal = '—'; }
      if (!(payload.phone || '').trim()) delete payload.smsConsent;
      await axios.put(`${API}/guest-portal/public/${token}`, payload);
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center p-6 text-center">
        <div>
          <Heart className="w-8 h-8 text-[#7B2C3B] mx-auto" />
          <h1 className="font-serif text-3xl mt-3">{error}</h1>
          <Link to="/" className="mt-5 inline-block text-sm text-[#7B2C3B] hover:underline">Back to Yourweddingfriends</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#7B2C3B]" /></div>;
  }

  const { guest, couple, config } = data;
  const cfg = config || {};
  const sections = cfg.sections || { plusOne: true, meal: true, dietary: true, address: true, phone: true, notes: true, customQuestion: false };
  const req = cfg.required || {};
  const plusOneCfg = cfg.plusOne || {
    question: 'Are you bringing a plus one?',
    helpText: "The couple has offered you a plus one — you're welcome to bring anyone.",
    guestNameLabel: "Your guest's name",
    guestNamePlaceholder: 'Their full name',
  };
  // Only show the plus-one question if the guest was flagged as eligible on
  // the Guests tab (guest.plusOne === true). Non-eligible guests never see it.
  const showPlusOne = !!guest.plusOne && sections.plusOne;
  const emailField = cfg.emailField || { alwaysShow: true, requiredIfMissing: true };
  const mergeVars = (s = '') => (s || '')
    .replace(/\{firstName\}/g, guest.name.split(' ')[0] || 'friend')
    .replace(/\{coupleNames\}/g, fmtCouple(couple, 'The couple'));
  const coupleName = fmtCouple(couple, 'The couple');
  // Parse YYYY-MM-DD as a local-time date (naive `new Date('2026-11-07')`
  // interprets it as UTC midnight → shifts back a day in negative-offset
  // timezones, showing Nov 6 for a Nov 7 wedding).
  const dateLabel = (() => {
    if (!couple.weddingDate) return '';
    const [y, m, d] = couple.weddingDate.split('-').map(Number);
    if (!y || !m || !d) return couple.weddingDate;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  })();

  if (submitted) {
    const referUrl = 'https://yourweddingfriends.com/?ref=guest';
    const shareText = `The couple I'm celebrating is using Yourweddingfriends to plan their wedding — free planner with guests, budget, RSVPs & site. Check it out: ${referUrl}`;
    const copyLink = async () => {
      try { await navigator.clipboard.writeText(referUrl); } catch (e) { /* ignore */ }
    };
    const nativeShare = async () => {
      if (navigator.share) {
        try { await navigator.share({ title: 'Yourweddingfriends', text: shareText, url: referUrl }); } catch (e) { /* user cancelled */ }
      } else {
        copyLink();
      }
    };
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="text-center soft-card rounded-3xl bg-white border border-[#EFE3D8] p-10">
            <div className="w-12 h-12 rounded-full bg-[#F4E4DA] flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-[#7B2C3B]" />
            </div>
            <h1 className="font-serif text-3xl mt-4 whitespace-pre-wrap">{linkifyText(mergeVars(cfg.thankYouMessage)) || `Thank you, ${guest.name.split(' ')[0]}!`}</h1>
            <p className="text-sm text-[#6A6A6A] mt-2">We&apos;ve sent your reply to {coupleName}. You can come back any time to update it.</p>
            <button data-testid="rsvp-edit-again" onClick={() => setSubmitted(false)} className="mt-6 text-sm text-[#7B2C3B] hover:underline">Edit my reply</button>
            <div className="mt-6 inline-flex items-center gap-1.5 text-[11px] text-[#9A8A7E]">
              <ShieldCheck className="w-3.5 h-3.5" /> Your details are only shared with the couple.
            </div>
          </div>

          {cfg.showReferralCard !== false && (
          <div data-testid="rsvp-share-card" className="mt-5 soft-card rounded-3xl border border-[#EFE3D8] p-6 bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-[#EFE3D8]">
                <Sparkles className="w-5 h-5 text-[#7B2C3B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="section-eyebrow text-[#7B2C3B]">You&apos;re invited too</p>
                <p className="font-serif text-lg text-[#2A2A2A] mt-0.5 leading-tight">Getting married? Know someone who is?</p>
                <p className="text-sm text-[#4A4A4A] mt-2">
                  {coupleName} plans their wedding on <strong>Yourweddingfriends</strong> — guests, budget, RSVPs and a beautiful wedding site in one place. Free to start.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    data-testid="rsvp-share-try"
                    href={referUrl}
                    target="_blank" rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full"
                  >
                    <Heart className="w-3.5 h-3.5" /> Try it free
                  </a>
                  <button
                    data-testid="rsvp-share-native"
                    onClick={nativeShare}
                    className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-white border border-[#EFE3D8] text-[#3A3A3A] hover:border-[#B8896C]"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share with a friend
                  </button>
                  <button
                    data-testid="rsvp-share-copy"
                    onClick={copyLink}
                    className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-white border border-[#EFE3D8] text-[#3A3A3A] hover:border-[#B8896C]"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy link
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF7F2]" data-testid="rsvp-portal">
      {/* Locked RSVP (auto-declined after reply-by). Renders instead of
          the form so guests can't sneak in a late Yes and blow up the
          seating chart. Couples can still manually flip them back from
          the dashboard. */}
      {guest.locked && (
        <div data-testid="rsvp-locked-state" className="min-h-screen flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F4E4DA] flex items-center justify-center">
              <Heart className="w-8 h-8 text-[#7B2C3B]" />
            </div>
            <h1 className="mt-6 font-serif text-3xl text-[#2A2A2A]">The RSVP window has closed</h1>
            <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">
              Hi {guest.name.split(' ')[0]} — {coupleName}&apos;s reply-by date has passed,
              so their guest list is now locked for planning. Your reply is currently
              recorded as <strong>Regretfully declines</strong>.
            </p>
            <p className="mt-3 text-xs text-[#6A6A6A]">
              If your plans have changed and you&apos;d still love to celebrate with them,
              please reach out to {coupleName} directly — they can update your reply
              from their end.
            </p>
          </div>
        </div>
      )}
      {!guest.locked && (<>
      {/* Already-replied popup — greets guests who click the RSVP link a
          second time (e.g. tapped the SMS earlier, now opening the email)
          so they don't feel forced to re-do the whole form. */}
      {showRepliedModal && (
        <div
          data-testid="rsvp-already-replied-modal"
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="max-w-sm w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#F4E4DA] flex items-center justify-center">
                <Heart className="w-7 h-7 text-[#7B2C3B] fill-[#7B2C3B]" />
              </div>
              <h2 className="mt-4 font-serif text-2xl text-[#2A2A2A]">
                We&apos;ve got your RSVP!
              </h2>
              <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed">
                Hi {guest.name.split(' ')[0]} — {coupleName} already received your reply{' '}
                <span className="font-medium text-[#7B2C3B]">
                  ({guest.rsvp === 'Yes' ? (cfg.attendingLabel || 'Happily Accepts') : (cfg.decliningLabel || 'Regretfully Declines')})
                </span>
                .
                {guest.rsvp === 'Yes' ? " We can't wait to celebrate with you! \uD83D\uDC95" : ' Thanks for letting us know.'}
              </p>
              <p className="mt-4 text-xs text-[#6A6A6A]">
                If your plans have changed, you can update your reply below — otherwise you&apos;re all set.
              </p>
            </div>
            <div className="p-4 border-t border-[#EFE3D8] bg-[#FBF7F2] grid grid-cols-1 gap-2">
              <button
                data-testid="rsvp-modal-all-set"
                onClick={() => { setShowRepliedModal(false); setSubmitted(true); }}
                className="w-full btn-primary py-2.5 rounded-full text-sm font-medium"
              >
                All set, thanks
              </button>
              <button
                data-testid="rsvp-modal-change-reply"
                onClick={() => setShowRepliedModal(false)}
                className="w-full py-2 rounded-full text-sm text-[#7B2C3B] hover:bg-[#F4E4DA]/60"
              >
                I need to change my reply
              </button>
            </div>
          </div>
        </div>
      )}
      <header className="px-6 lg:px-10 pt-6 pb-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#7B2C3B] flex items-center justify-center">
            <Heart className="w-4 h-4 text-[#FBF7F2] fill-[#FBF7F2]" />
          </div>
          <span className="font-serif text-base font-semibold">Yourwedding<span className="italic text-[#7B2C3B]">friends</span></span>
        </Link>
        <span className="inline-flex items-center gap-1.5 text-xs text-[#6A6A6A]">
          <ShieldCheck className="w-3.5 h-3.5 text-[#7B2C3B]" /> Private RSVP
        </span>
      </header>

      <div className="px-6 lg:px-10 py-10">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8 animate-float-up">
            <p className="section-eyebrow">Hello, {guest.name.split(' ')[0]}</p>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[#2A2A2A]">{coupleName}</h1>
            {dateLabel && (
              <p className="mt-3 text-sm inline-flex items-center gap-2 text-[#4A4A4A]">
                <Calendar className="w-4 h-4" /> {dateLabel}
              </p>
            )}
            {(couple.venue || couple.location) && (
              <p className="text-xs inline-flex items-center gap-1.5 mt-1 text-[#6A6A6A]">
                <MapPin className="w-3.5 h-3.5" /> {[couple.venue, couple.location].filter(Boolean).join(' · ')}
              </p>
            )}
            {couple.venueAddress && (
              <p className="text-xs mt-0.5">
                <a
                  data-testid="rsvp-venue-address"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(couple.venueAddress)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#7B2C3B] hover:underline"
                >
                  {couple.venueAddress}
                </a>
              </p>
            )}
            {/* Venue photo — optional, shown below the address so guests can
                visually recognize the spot before they arrive. Sized to the
                RSVP layout (max ~640px wide, 16:9 rounded frame). */}
            {couple.venuePhoto && cfg.showVenuePhoto !== false && (
              <div className="mt-4 max-w-md mx-auto">
                <img
                  data-testid="rsvp-venue-photo"
                  src={couple.venuePhoto}
                  alt={couple.venue || 'Wedding venue'}
                  className="w-full aspect-[16/9] object-cover rounded-2xl border border-[#EFE3D8] shadow-sm"
                />
              </div>
            )}
          </div>

          {cfg.welcomeMessage && (
            <p data-testid="rsvp-welcome" className="text-center text-sm text-[#4A4A4A] max-w-md mx-auto mb-6 italic">
              {mergeVars(cfg.welcomeMessage)}
            </p>
          )}

          {(guest.rsvp === 'Yes' || guest.rsvp === 'No') && !submitted && (
            <div data-testid="rsvp-update-banner" className="mb-5 rounded-2xl border border-[#EBD3C2] bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2] px-4 py-3 text-sm">
              <p className="font-medium text-[#7B2C3B]">
                You&apos;ve already replied — <span className="capitalize">{guest.rsvp === 'Yes' ? (cfg.attendingLabel || 'Happily accepts') : (cfg.decliningLabel || 'Regretfully declines')}</span>.
              </p>
              <p className="text-[12px] text-[#4A4A4A] mt-0.5">
                Plans changed? Update your answer below and we&apos;ll let the couple know.
              </p>
            </div>
          )}

          <form onSubmit={submit} className="soft-card rounded-3xl bg-white border border-[#EFE3D8] p-6 space-y-5">
            <div>
              <p className="text-sm font-medium text-[#2A2A2A] mb-2">{cfg.attendanceQuestion || 'Will you be joining us?'}</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ['Yes', cfg.attendingLabel || 'Happily Accepts'],
                  ['No', cfg.decliningLabel || 'Regretfully Declines'],
                ].map(([opt, label]) => (
                  <button
                    type="button"
                    key={opt}
                    data-testid={`rsvp-${opt.toLowerCase()}`}
                    onClick={() => setForm({ ...form, rsvp: opt })}
                    className={`py-3 px-3 rounded-xl text-sm font-medium transition-all border ${form.rsvp === opt ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'border-[#EFE3D8] bg-white text-[#2A2A2A] hover:border-[#B8896C]'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {showPlusOne && (
            <div className="rounded-xl border border-[#EFE3D8] overflow-hidden">
              <div className="p-3">
                <p className="text-sm text-[#2A2A2A]">{plusOneCfg.question}</p>
                {plusOneCfg.helpText && (
                  <p className="text-[11px] text-[#6A6A6A] mt-0.5">{plusOneCfg.helpText}</p>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    data-testid="rsvp-plus-one-yes"
                    type="button"
                    onClick={() => setForm({ ...form, plusOneComing: true, plusOneName: form.plusOneName || (data.guest.plusOneName || '') })}
                    className={`px-3 py-2 rounded-full text-sm font-medium border transition-colors ${form.plusOneComing === true ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
                  >
                    Yes, bringing a guest
                  </button>
                  <button
                    data-testid="rsvp-plus-one-no"
                    type="button"
                    onClick={() => setForm({ ...form, plusOneComing: false, plusOneName: '' })}
                    className={`px-3 py-2 rounded-full text-sm font-medium border transition-colors ${form.plusOneComing === false ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
                  >
                    No, just me
                  </button>
                </div>
              </div>
              {form.plusOneComing === true && (
                <div className="border-t border-[#EFE3D8] p-3 bg-[#FBF7F2]/60">
                  <label className="text-[11px] uppercase tracking-wide text-[#6A6A6A]">{plusOneCfg.guestNameLabel}</label>
                  <input
                    data-testid="rsvp-plus-one-name"
                    value={form.plusOneName}
                    onChange={(e) => setForm({ ...form, plusOneName: e.target.value })}
                    placeholder={plusOneCfg.guestNamePlaceholder || 'Their full name'}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                  />
                </div>
              )}
            </div>
            )}

            {sections.meal && cfg.mealMode !== 'preset' && form.rsvp !== 'No' && (
            <div>
              <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Meal preference{req.meal && <span className="text-[#7B2C3B] ml-1">*required</span>}</label>
              <select
                data-testid="rsvp-meal"
                required={!!req.meal}
                value={form.meal}
                onChange={(e) => setForm({ ...form, meal: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm"
              >
                <option>—</option>
                {(cfg.mealOptions || ['Meat', 'Fish', 'Vegetarian', 'Vegan', 'Kids']).map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
              {showPlusOne && form.plusOneComing === true && (
                <>
                  <label className="block mt-3 text-xs uppercase tracking-wide text-[#6A6A6A]">Meal for {form.plusOneName || 'your guest'}</label>
                  <select
                    data-testid="rsvp-plus-one-meal"
                    value={form.plusOneMeal}
                    onChange={(e) => setForm({ ...form, plusOneMeal: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm"
                  >
                    <option>—</option>
                    {(cfg.mealOptions || ['Meat', 'Fish', 'Vegetarian', 'Vegan', 'Kids']).map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </>
              )}
            </div>
            )}

            {sections.meal && cfg.mealMode === 'preset' && cfg.mealPresetNote && (
            <div data-testid="rsvp-meal-preset" className="rounded-xl border border-[#EFE3D8] bg-[#FBF7F2] px-3 py-2.5 text-sm text-[#4A4A4A] italic">
              {cfg.mealPresetNote}
            </div>
            )}

            {sections.dietary && (
            <div>
              <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Dietary needs / allergies{req.dietary && <span className="text-[#7B2C3B] ml-1">*required</span>}</label>
              <input
                data-testid="rsvp-dietary"
                required={!!req.dietary}
                value={form.dietary}
                onChange={(e) => setForm({ ...form, dietary: e.target.value })}
                placeholder="e.g. gluten-free, nut allergy…"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
            </div>
            )}

            {emailField.alwaysShow !== false && (
            <div>
              <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">
                Email address
                {emailWasBlank && emailField.requiredIfMissing !== false && <span className="text-[#7B2C3B] ml-1">*required</span>}
              </label>
              <input
                data-testid="rsvp-email"
                type="email"
                value={form.email}
                required={emailWasBlank && emailField.requiredIfMissing !== false}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
              {emailWasBlank && emailField.requiredIfMissing !== false && (
                <p className="text-[11px] text-[#6A6A6A] mt-1">We&apos;ll send you the full invitation and confirmation here.</p>
              )}
            </div>
            )}

            {sections.address && (
            <div>
              <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Mailing address{req.address && <span className="text-[#7B2C3B] ml-1">*required</span>}</label>
              <textarea
                data-testid="rsvp-address"
                required={!!req.address}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
                placeholder="123 Main St, Apt 4, City, State, ZIP"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
            </div>
            )}

            {sections.phone && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Phone{req.phone ? <span className="text-[#7B2C3B] ml-1">*required</span> : <span className="text-[#9A8A7E]"> (optional)</span>}</label>
                <input
                  data-testid="rsvp-phone"
                  required={!!req.phone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="555-555-5555"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
                />
              </div>
              {(form.phone || '').trim() && (
                <div className="sm:col-span-2">
                  <SmsConsentCheckbox testId="rsvp-sms-consent" checked={form.smsConsent} onChange={(v) => setForm({ ...form, smsConsent: v })} />
                </div>
              )}
            </div>
            )}

            {sections.customQuestion && cfg.customQuestion?.label && (
            <div>
              <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">{cfg.customQuestion.label}{req.customQuestion && <span className="text-[#7B2C3B] ml-1">*required</span>}</label>
              <input
                data-testid="rsvp-custom-answer"
                required={!!req.customQuestion}
                value={form.customAnswer}
                onChange={(e) => setForm({ ...form, customAnswer: e.target.value })}
                placeholder={cfg.customQuestion.placeholder || ''}
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
            </div>
            )}

            {sections.notes && (
            <div>
              <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">Anything else for the couple?{req.notes && <span className="text-[#7B2C3B] ml-1">*required</span>}</label>
              <textarea
                data-testid="rsvp-notes"
                required={!!req.notes}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="A song you'd love to dance to, a kind word, a heads-up…"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
            </div>
            )}

            <button
              data-testid="rsvp-submit"
              type="submit"
              disabled={submitting || form.rsvp === 'Pending'}
              className="w-full btn-primary py-3 rounded-full text-sm font-medium disabled:opacity-60"
            >
              {submitting ? 'Sending…' : (form.rsvp === 'Pending' ? 'Pick an answer above first' : (cfg.submitLabel || 'Send my reply'))}
            </button>
            <p className="text-[11px] text-center text-[#9A8A7E] inline-flex items-center justify-center gap-1.5 w-full">
              <ShieldCheck className="w-3 h-3" /> Only the couple sees your reply. We never share it.
            </p>
          </form>

          <TravelCard travel={data.travel} siteSlug={data.siteSlug} />
        </div>
      </div>
      </>)}
    </div>
  );
}

function TravelCard({ travel, siteSlug }) {
  if (!travel) return null;
  const hotels = travel.hotels || [];
  const rideshare = travel.rideshare || {};
  const providers = (rideshare.providers || []).filter((p) => p.name);
  const hasAnything = travel.note || hotels.length > 0 || providers.length > 0 || rideshare.shuttleNote;
  if (!hasAnything) return null;
  return (
    <div data-testid="rsvp-travel-card" className="mt-6 soft-card rounded-3xl bg-white border border-[#EFE3D8] p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl text-[#2A2A2A] inline-flex items-center gap-2">
          <Hotel className="w-4 h-4 text-[#7B2C3B]" /> Where to stay &amp; how to get there
        </h2>
        {siteSlug && (
          <Link to={`/wedding/${siteSlug}`} className="text-[11px] text-[#7B2C3B] inline-flex items-center gap-1 hover:underline">
            Full site <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>

      {travel.note && (
        <p className="text-sm text-[#4A4A4A] whitespace-pre-wrap mb-4">{travel.note}</p>
      )}

      {hotels.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-2 inline-flex items-center gap-1">
            <Hotel className="w-3 h-3" /> Recommended hotels
          </p>
          <div className="space-y-2">
            {hotels.map((h, i) => (
              <div key={i} data-testid={`rsvp-hotel-${i}`} className="p-3 rounded-xl border border-[#EFE3D8] bg-[#FBF7F2]/50">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-[#2A2A2A]">{h.name}</p>
                    {h.address && <p className="text-xs text-[#6A6A6A] mt-0.5">{h.address}</p>}
                    <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-[#6A6A6A]">
                      {h.distance && <span>{h.distance}</span>}
                      {h.priceRange && <span>· {h.priceRange}</span>}
                      {h.notes && <span>· {h.notes}</span>}
                    </div>
                  </div>
                  {h.url && (
                    <a href={h.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#7B2C3B] flex-shrink-0 hover:underline">
                      Book <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {providers.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-2 inline-flex items-center gap-1">
            <Car className="w-3 h-3" /> Rides
          </p>
          <div className="grid grid-cols-2 gap-2">
            {providers.map((p, i) => (
              <div key={i} data-testid={`rsvp-rideshare-${i}`} className="p-3 rounded-xl border border-[#EFE3D8] text-sm">
                <p className="font-medium text-[#2A2A2A]">{p.name}</p>
                {p.promoCode && <p className="text-[11px] text-[#6A6A6A] mt-0.5">Promo: <strong className="text-[#7B2C3B]">{p.promoCode}</strong></p>}
                {p.note && <p className="text-[11px] text-[#6A6A6A] mt-0.5">{p.note}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {rideshare.shuttleNote && (
        <div className="p-3 rounded-xl border border-[#EFE3D8] bg-[#F4E4DA] text-sm">
          <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#7B2C3B] mb-1">
            <Bus className="w-3 h-3" /> Wedding shuttle
          </p>
          <p className="text-sm text-[#3A3A3A]">{rideshare.shuttleNote}</p>
          {rideshare.shuttleUrl && (
            <a href={rideshare.shuttleUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#7B2C3B] hover:underline">
              Details <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}
